// components/Contracts/ListContracts.js (Req 1, 2026-05-13)
// Lista de contratos emisor↔tercero. Permite:
//  - Filtrar por estado (Pendiente/Aprobado/Rechazado)
//  - Crear un contrato nuevo (modal de upload)
//  - Cambiar estado de un contrato existente
//  - Descargar el binario
//  - Eliminar (con validación backend)
import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import {
  getContracts,
  createContractMultipart,
  changeContractStatus,
  deleteContract,
  triggerDownloadContract,
  previewContract,
} from "../../controllers/apiContracts";
import { getClients, normalizeClientsResponse } from "../../controllers/api_clients";
import { swalConfirm } from "../../utils/swal";

const ESTADO_COLORS = {
  Pendiente: "bg-yellow-100 text-yellow-800",
  Aprobado: "bg-green-100 text-green-800",
  Rechazado: "bg-red-100 text-red-800",
};

export default function ListContracts() {
  const { user } = useContext(AuthContext);
  const rol = user?.rol || "";

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");

  // Req 1 v4 (2026-05-13): filtro por empresa_id (emisor) — solo admin/auditor
  // tienen acceso global y necesitan poder filtrar por una empresa específica.
  // Para operador_admin/visor el backend ya filtra por su cliente automáticamente.
  const [filtroClienteId, setFiltroClienteId] = useState("");
  const [emisoresList, setEmisoresList] = useState([]);
  const esRolGlobal = rol === 'admin' || rol === 'auditor';

  // Estado del modal de creación
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [terceros, setTerceros] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    tercero_id: "",
    empresa_id: "",
    file: null,
    observaciones: "",
    comision_servicio: "",
  });
  const [uploading, setUploading] = useState(false);

  // Modal de cambio de estado
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({
    id: null,
    estado: "Pendiente",
    observaciones: "",
    comision_servicio: "",
  });

  // Req 1 v2 (2026-05-13): visor de PDF/imagen inline (sin descargar)
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewState, setPreviewState] = useState({
    url: null,
    contentType: null,
    filename: null,
    loading: false,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      // Req 1 v4: solo admin/auditor pueden enviar empresa_id; para los demás
      // el backend infiere el cliente desde el JWT.
      if (esRolGlobal && filtroClienteId) params.empresa_id = filtroClienteId;
      const data = await getContracts(params);
      setContracts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Error al obtener contratos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [filtroEstado, filtroClienteId]); // refresh es estable dentro de este componente

  // Req 1 v4: cargar lista de emisores para el dropdown (solo admin/auditor).
  useEffect(() => {
    if (!esRolGlobal) return;
    (async () => {
      try {
        const data = await getClients({ es_emisor: true });
        setEmisoresList(normalizeClientsResponse(data));
      } catch (e) {
        console.warn("Error al cargar emisores para el filtro:", e);
      }
    })();
  }, [esRolGlobal]);

  // Cargar terceros para poblar el dropdown del modal de upload.
  // (2026-05-13) Para admin/auditor se requiere `emisorId` para filtrar los
  // terceros del emisor seleccionado; sin emisor, no se hace ninguna llamada.
  // Para operador/operador_admin/visor el backend ya filtra por su propio
  // cliente automaticamente (padre_id = su cliente) — emisorId queda ignorado.
  const cargarTerceros = async (emisorId) => {
    try {
      // (2026-05-13) sin_contrato_activo=true → excluye terceros que ya tienen
      // un contrato en estado Pendiente o Aprobado. El modal solo ofrece
      // terceros sin contrato vigente / pendiente (incluye terceros sin
      // contratos o con contratos Rechazados).
      const filtros = { es_tercero: true, sin_contrato_activo: true };
      if (esRolGlobal) {
        if (!emisorId) {
          setTerceros([]);
          return;
        }
        filtros.padre_id = emisorId;
      }
      const data = await getClients(filtros);
      setTerceros(normalizeClientsResponse(data));
    } catch (e) {
      toast.error("Error al cargar lista de terceros");
    }
  };

  const openUploadModal = () => {
    setUploadForm({
      tercero_id: "",
      // Para operador: empresa_id es el propio (su empresa es el emisor).
      // Para admin/auditor: queda vacio hasta que se seleccione un emisor.
      empresa_id: esRolGlobal ? "" : (user?.empresa_id || ""),
      file: null,
      observaciones: "",
      comision_servicio: "",
    });
    setShowUploadModal(true);
    if (esRolGlobal) {
      // Admin debe primero elegir el emisor en el modal; los terceros se
      // cargan al cambiar el select de emisor.
      setTerceros([]);
    } else {
      cargarTerceros();
    }
  };

  const submitUpload = async () => {
    if (!uploadForm.empresa_id) {
      toast.error(
        esRolGlobal
          ? "Seleccione el cliente emisor"
          : "Falta empresa_id (emisor). Recargue la página."
      );
      return;
    }
    if (!uploadForm.tercero_id) {
      toast.error("Seleccione un tercero");
      return;
    }
    if (!uploadForm.file) {
      toast.error("Adjunte el archivo del contrato");
      return;
    }
    setUploading(true);
    try {
      await createContractMultipart({
        empresa_id: uploadForm.empresa_id,
        tercero_id: uploadForm.tercero_id,
        file: uploadForm.file,
        observaciones: uploadForm.observaciones,
        comision_servicio: uploadForm.comision_servicio,
      });
      toast.success("Contrato subido. Estado: Pendiente.");
      setShowUploadModal(false);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.error || "Error al subir el contrato";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const openStatusModal = (c) => {
    setStatusForm({
      id: c.id,
      estado: c.estado,
      observaciones: c.observaciones || "",
      // Pre-cargar la comisión actual del contrato (puede ser null)
      comision_servicio: c.comision_servicio != null ? String(c.comision_servicio) : "",
    });
    setShowStatusModal(true);
  };

  const submitStatus = async () => {
    // Para aprobar exigimos comision_servicio (el backend valida también, pero
    // damos feedback inmediato en el cliente).
    if (statusForm.estado === "Aprobado" && (!statusForm.comision_servicio || isNaN(parseFloat(statusForm.comision_servicio)))) {
      toast.error("Para aprobar el contrato debe ingresar la comisión (%).");
      return;
    }
    try {
      await changeContractStatus(
        statusForm.id,
        statusForm.estado,
        statusForm.observaciones,
        statusForm.comision_servicio,
      );
      toast.success(`Estado actualizado a "${statusForm.estado}".`);
      setShowStatusModal(false);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.error || "Error al cambiar el estado";
      toast.error(msg);
    }
  };

  // Req 1 v2: abrir visor inline (PDF/imagen). Libera el blob URL al cerrar.
  const openPreviewModal = async (c) => {
    setPreviewState({ url: null, contentType: null, filename: null, loading: true });
    setShowPreviewModal(true);
    try {
      const { url, contentType, filename } = await previewContract(c.id);
      setPreviewState({ url, contentType, filename, loading: false });
    } catch (e) {
      toast.error("Error al cargar el contrato para vista previa");
      setShowPreviewModal(false);
      setPreviewState({ url: null, contentType: null, filename: null, loading: false });
    }
  };

  const closePreviewModal = () => {
    if (previewState.url) {
      try { window.URL.revokeObjectURL(previewState.url); } catch (_) {}
    }
    setShowPreviewModal(false);
    setPreviewState({ url: null, contentType: null, filename: null, loading: false });
  };

  const onDelete = async (c) => {
    const ok = await swalConfirm(
      "Eliminar contrato",
      `¿Eliminar el contrato "${c.contrato_filename}"? Esta acción no se puede deshacer.`,
      { confirmText: "Eliminar", cancelText: "Cancelar", danger: true }
    );
    if (!ok) return;
    try {
      await deleteContract(c.id);
      toast.success("Contrato eliminado.");
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.error || "Error al eliminar";
      toast.error(msg);
    }
  };

  const canWrite = rol !== "auditor"; // Auditor solo lectura
  // (2026-05-15) Cambiar estado del contrato (aprobar/rechazar + comisión):
  // exclusivo de admin. El resto solo ve / elimina.
  const canChangeEstado = rol === "admin";
  const totalsByStatus = useMemo(() => {
    const acc = { Pendiente: 0, Aprobado: 0, Rechazado: 0 };
    contracts.forEach((c) => {
      if (acc[c.estado] != null) acc[c.estado]++;
    });
    return acc;
  }, [contracts]);

  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-twilight-indigo-700">
            Contratos de Terceros
          </h2>
          <p className="text-sm text-slate-500">
            Documentos firmados entre el emisor y sus terceros. Un tercero
            requiere al menos un contrato Aprobado para poder activarse.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={openUploadModal}
            className="bg-twilight-indigo-600 hover:bg-twilight-indigo-700 text-white px-4 py-2 rounded shadow"
          >
            <i className="fas fa-upload mr-2" />
            Subir contrato
          </button>
        )}
      </div>

      {/* Resumen por estado */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {["Pendiente", "Aprobado", "Rechazado"].map((est) => (
          <div
            key={est}
            className={`p-3 rounded ${ESTADO_COLORS[est]} flex items-center justify-between`}
          >
            <span className="font-bold">{est}</span>
            <span className="text-xl font-bold">{totalsByStatus[est]}</span>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        {/* Filtro de estado (todos los roles) */}
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">Estado</label>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="Pendiente">Pendiente</option>
            <option value="Aprobado">Aprobado</option>
            <option value="Rechazado">Rechazado</option>
          </select>
        </div>

        {/* Req 1 v4 (2026-05-13): filtro por emisor — solo admin/auditor */}
        {esRolGlobal && (
          <div className="min-w-[260px]">
            <label className="block text-xs font-bold text-slate-600 mb-1">
              Empresa emisora
              <span className="ml-2 text-[10px] font-normal text-slate-400">
                ({emisoresList.length} disponibles)
              </span>
            </label>
            <select
              className="border rounded px-2 py-1 text-sm w-full"
              value={filtroClienteId}
              onChange={(e) => setFiltroClienteId(e.target.value)}
            >
              <option value="">— Todos los emisores —</option>
              {emisoresList.map((em) => (
                <option key={em.id} value={em.id}>
                  {em.nombre_empresa} ({em.rif})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Botón limpiar filtros (cosmético) */}
        {(filtroEstado || filtroClienteId) && (
          <button
            onClick={() => { setFiltroEstado(""); setFiltroClienteId(""); }}
            className="text-xs text-twilight-indigo-600 hover:underline"
            title="Limpiar filtros"
          >
            <i className="fas fa-times-circle mr-1" />Limpiar
          </button>
        )}
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Cargando contratos...</div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          No hay contratos {filtroEstado ? `en estado "${filtroEstado}"` : ""}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-100">
              <tr>
                {/* (2026-05-13) Columna ID oculta — el id sigue disponible internamente
                    via key={c.id} y los handlers, pero no se muestra al usuario. */}
                {/* (2026-05-13) Para admin/auditor: RIF + Empresa (Afiliada) del
                    cliente emisor del contrato, en columnas separadas para
                    consistencia con las otras listas del sistema. */}
                {esRolGlobal && (
                  <>
                    <th className="text-left p-2 text-xs uppercase">RIF (Afiliada)</th>
                    <th className="text-left p-2 text-xs uppercase">Empresa (Afiliada)</th>
                  </>
                )}
                <th className="text-left p-2 text-xs uppercase">Tercero</th>
                <th className="text-left p-2 text-xs uppercase">Archivo</th>
                <th className="text-left p-2 text-xs uppercase">Tamaño</th>
                <th className="text-left p-2 text-xs uppercase">Comisión</th>
                <th className="text-left p-2 text-xs uppercase">Estado</th>
                <th className="text-left p-2 text-xs uppercase">Creado</th>
                <th className="text-left p-2 text-xs uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-b hover:bg-slate-50">
                  {esRolGlobal && (
                    <>
                      <td className="p-2 text-sm">
                        {c.empresa && c.empresa.rif
                          ? c.empresa.rif
                          : <span className="text-blueGray-400 text-xs">—</span>}
                      </td>
                      <td className="p-2 text-sm">
                        {c.empresa && c.empresa.nombre_empresa
                          ? c.empresa.nombre_empresa
                          : <span className="text-blueGray-400 text-xs">—</span>}
                      </td>
                    </>
                  )}
                  <td className="p-2">
                    {c.tercero
                      ? `${c.tercero.nombre_empresa} (${c.tercero.rif})`
                      : c.tercero_id}
                  </td>
                  <td className="p-2">{c.contrato_filename}</td>
                  <td className="p-2">
                    {Math.round((c.contrato_size_bytes || 0) / 1024)} KB
                  </td>
                  <td className="p-2">
                    {c.comision_servicio != null ? (
                      <span className="font-mono text-xs">{c.comision_servicio}%</span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${ESTADO_COLORS[c.estado] || ""}`}>
                      {c.estado}
                    </span>
                  </td>
                  <td className="p-2 text-xs text-slate-500">
                    {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                  </td>
                  <td className="p-2">
                    <button
                      onClick={() => openPreviewModal(c)}
                      className="text-slate-700 hover:underline mr-2"
                      title="Vista previa"
                    >
                      <i className="fas fa-eye" />
                    </button>
                    <button
                      onClick={() => triggerDownloadContract(c.id)}
                      className="text-twilight-indigo-600 hover:underline mr-2"
                      title="Descargar"
                    >
                      <i className="fas fa-download" />
                    </button>
                    {/* (2026-05-15) Cambiar estado/comisión del contrato:
                        SOLO admin. operador_admin/visor pueden ver/eliminar
                        pero no aprobar/rechazar contratos. */}
                    {canChangeEstado && (
                      <button
                        onClick={() => openStatusModal(c)}
                        className="text-blue-600 hover:underline mr-2"
                        title="Cambiar estado / comisión"
                      >
                        <i className="fas fa-edit" />
                      </button>
                    )}
                    {canWrite && (
                      <button
                        onClick={() => onDelete(c)}
                        className="text-red-600 hover:underline"
                        title="Eliminar"
                      >
                        <i className="fas fa-trash" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: subir contrato */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-4">Subir contrato</h3>

            {/* (2026-05-13) Para admin/auditor: primero seleccionar el emisor;
                la lista de terceros se recarga filtrada por padre_id=emisor.
                Para operador, el emisor es su propia empresa (no se muestra). */}
            {esRolGlobal && (
              <div className="mb-3">
                <label className="block text-sm font-bold mb-1">Empresa emisora *</label>
                <select
                  className="border rounded w-full px-2 py-1"
                  value={uploadForm.empresa_id}
                  onChange={(e) => {
                    const nuevoEmisor = e.target.value;
                    setUploadForm({
                      ...uploadForm,
                      empresa_id: nuevoEmisor,
                      tercero_id: "", // resetear el tercero al cambiar de emisor
                    });
                    cargarTerceros(nuevoEmisor);
                  }}
                >
                  <option value="">-- Seleccione --</option>
                  {emisoresList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre_empresa} ({c.rif}) {c.activo ? "" : "[Inactivo]"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">
                Tercero *
                {esRolGlobal && !uploadForm.empresa_id && (
                  <span className="text-slate-500 text-xs ml-1">
                    (seleccione primero el cliente emisor)
                  </span>
                )}
              </label>
              <select
                className="border rounded w-full px-2 py-1 disabled:bg-slate-100 disabled:cursor-not-allowed"
                value={uploadForm.tercero_id}
                disabled={esRolGlobal && !uploadForm.empresa_id}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, tercero_id: e.target.value })
                }
              >
                <option value="">-- Seleccione --</option>
                {terceros.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre_empresa} ({t.rif}) {t.activo ? "" : "[Inactivo]"}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">
                Archivo (PDF/imagen, máx. 10 MB) *
              </label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, file: e.target.files[0] })
                }
              />
              {uploadForm.file && (
                <p className="text-xs text-slate-500 mt-1">
                  {uploadForm.file.name} — {Math.round(uploadForm.file.size / 1024)} KB
                </p>
              )}
            </div>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">
                Comisión del servicio (%)
                <span className="text-slate-500 text-xs ml-1">(opcional al subir; se exige al aprobar)</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className="border rounded w-full px-2 py-1"
                placeholder="0.00"
                value={uploadForm.comision_servicio}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, comision_servicio: e.target.value })
                }
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">Observaciones</label>
              <textarea
                className="border rounded w-full px-2 py-1"
                rows="2"
                value={uploadForm.observaciones}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, observaciones: e.target.value.toUpperCase() })
                }
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 border rounded hover:bg-slate-100"
                disabled={uploading}
              >
                Cancelar
              </button>
              <button
                onClick={submitUpload}
                disabled={uploading}
                className="px-4 py-2 bg-twilight-indigo-600 text-white rounded hover:bg-twilight-indigo-700 disabled:opacity-50"
              >
                {uploading ? "Subiendo..." : "Subir"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: cambiar estado y comisión */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Cambiar estado del contrato</h3>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">Nuevo estado</label>
              <select
                className="border rounded w-full px-2 py-1"
                value={statusForm.estado}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, estado: e.target.value })
                }
              >
                <option value="Pendiente">Pendiente</option>
                <option value="Aprobado">Aprobado</option>
                <option value="Rechazado">Rechazado</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">
                Comisión del servicio (%)
                {statusForm.estado === "Aprobado" && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className="border rounded w-full px-2 py-1"
                placeholder="0.00"
                value={statusForm.comision_servicio}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, comision_servicio: e.target.value })
                }
              />
              {statusForm.estado === "Aprobado" && (
                <p className="text-xs text-amber-700 mt-1">
                  <i className="fas fa-info-circle mr-1" />
                  Al aprobar, este valor se replicará al cliente tercero como su
                  comisión de servicio vigente.
                </p>
              )}
            </div>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1">Observaciones</label>
              <textarea
                className="border rounded w-full px-2 py-1"
                rows="3"
                value={statusForm.observaciones}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, observaciones: e.target.value.toUpperCase() })
                }
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 border rounded hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                onClick={submitStatus}
                className="px-4 py-2 bg-twilight-indigo-600 text-white rounded hover:bg-twilight-indigo-700"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Req 1 v2: Modal de vista previa (PDF/imagen inline) */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-bold truncate">
                <i className="fas fa-eye mr-2 text-twilight-indigo-600" />
                Vista previa: {previewState.filename || "..."}
              </h3>
              <div className="flex gap-2">
                {previewState.url && (
                  <a
                    href={previewState.url}
                    download={previewState.filename}
                    className="text-twilight-indigo-600 hover:underline text-sm"
                    title="Descargar"
                  >
                    <i className="fas fa-download mr-1" />
                    Descargar
                  </a>
                )}
                <button onClick={closePreviewModal} className="text-slate-500 hover:text-slate-700">
                  <i className="fas fa-times text-xl" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-100">
              {previewState.loading && (
                <div className="h-full flex items-center justify-center text-slate-500">
                  <i className="fas fa-spinner fa-spin mr-2" />
                  Cargando vista previa...
                </div>
              )}
              {previewState.url && !previewState.loading && (
                (previewState.contentType || "").startsWith("image/") ? (
                  <div className="h-full flex items-center justify-center p-4">
                    <img
                      src={previewState.url}
                      alt={previewState.filename}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (previewState.contentType || "").includes("pdf") ? (
                  <iframe
                    src={previewState.url}
                    title={previewState.filename}
                    className="w-full h-full border-0"
                  />
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-600">
                    <i className="fas fa-file text-5xl mb-3" />
                    <p>El navegador no puede mostrar este tipo de archivo en línea.</p>
                    <p className="text-sm text-slate-500">{previewState.contentType}</p>
                    <a
                      href={previewState.url}
                      download={previewState.filename}
                      className="mt-3 px-4 py-2 bg-twilight-indigo-600 text-white rounded hover:bg-twilight-indigo-700"
                    >
                      Descargar archivo
                    </a>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
